import React, { useState, useRef, useMemo, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import TableFooter from './footer'
import TableLoading from 'shared/tables/table-loading'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import TableFilters from 'transactions/table/filters/index'
import TableHeader from 'transactions/table/header'
import TableRow from 'transactions/table/row'
import useTransactions from 'api/hooks/use-transactions'
import DownloadTransactions from './actions'
import { useTableColumns } from 'api/hooks/use-table-columns'
import UserContext from 'context/user-context'
import AuthContext from 'context/auth-context'
import { transactionFilterParams } from 'api/transaction-query-params'
import { useQuery } from 'react-query'
import customColumnService from 'services/customColumnService'

const defaultTableFilters = {
  page: 1,
  perPage: 50,
  orderBy: '',
  orderDirection: '',
  external_id: '',
  department_id: '',
  vendors: [],
  excludedVendors: [],
  section_id: '',
  employees: [],
  excludedEmployees: [],
  analytics: [],
  startOn: '',
  endOn: '',
  unviewedOnly: false,
  open: false,
  inProgress: false,
  exception: false,
  auditReportId: '',
  dashboardId: '',
  showCleared: '',
  saved: '',
  orgId: '',
  objectId: '',
  segOne: '',
  segTwo: '',
  segThree: '',
  segFour: '',
  segFive: '',
  orgs: [],
  objects: [],
  projects: [],
  segOnes: [],
  segTwos: [],
  segThrees: [],
  segFours: [],
  segFives: [],
  segSixes: [],
  segSevens: [],
  segEights: [],
  orgsOperator: '',
  objectsOperator: '',
  projectsOperator: '',
  segOnesOperator: '',
  segTwosOperator: '',
  segThreesOperator: '',
  segFoursOperator: '',
  segFivesOperator: '',
  segSixesOperator: '',
  segSevensOperator: '',
  segEightsOperator: '',
  tag: '',
  disableJump: false,
  dateRangeSelected: '',
  flagCount: '',
  analyticsCount: '',
  transactionListId: ''
}

function TransactionTable({
  sectionId,
  perPage,
  isFilterOpen = false,
  columnModalOpen = false,
  vendors = [],
  excludedVendors = [],
  employees = [],
  excludedEmployees = [],
  minimumAmount = '',
  maximumAmount = '',
  analytic = [],
  orderBy = '',
  orderDirection = '',
  includeOrdering = false,
  columns,
  filtersEnabled = true,
  filters,
  footerEnabled = true,
  auditReportId = '',
  dashboardId = '',
  dashboardSetParams = null,
  showCleared = '',
  saved = '',
  title = '',
  source = '',
  orgId = '',
  objectId = '',
  segOne = '',
  segTwo = '',
  segThree = '',
  segFour = '',
  segFive = '',
  orgs = [],
  objects = [],
  projects = [],
  segOnes = [],
  segTwos = [],
  segThrees = [],
  segFours = [],
  segFives = [],
  segSixes = [],
  segSevens = [],
  segEights = [],
  orgsOperator = '',
  objectsOperator = '',
  projectsOperator = '',
  segOnesOperator = '',
  segTwosOperator = '',
  segThreesOperator = '',
  segFoursOperator = '',
  segFivesOperator = '',
  segSixesOperator = '',
  segSevensOperator = '',
  segEightsOperator = '',
  tag = '',
  startOn = '',
  endOn = '',
  downloadButton = true,
  defaultParams = {},
  riskScore = '',
  analyticsCount = '',
  disableJump = false,
  analyticsColumns = [],
  queryParams = {},
  precomputedColumnOrder = null,
  canEdit = true,
  referenceSearch = null,
  onReferenceSearchHandled = null,
  reportFeatures = [],
  showCustomColumns = true,
  showAddToListColumn = true,
  onTransactionListIdChange = null,
  // Sent as-is on the rows request only, e.g. a chart drill-down's chart_id and drilldown.
  requestParams = null
}) {
  const { data: tableColumns } = useTableColumns({
    params: { section_id: sectionId },
    options: { refetchOnWindowFocus: false }
  })

  const { municipality } = useContext(UserContext)
  const token = useContext(AuthContext)

  // Use the passed columns in their original order
  const columnOrder = columns

  // eslint-disable-next-line
  const memoizedQueryParams = useMemo(
    () => queryParams,
    // eslint-disable-next-line
    [JSON.stringify(queryParams)]
  )

  const [tableFilters, setTableFilters] = useState({
    ...defaultTableFilters,
    section_id: sectionId,
    employees: employees || [],
    excludedEmployees: excludedEmployees || [],
    vendors: vendors || [],
    excludedVendors: excludedVendors || [],
    page: defaultParams.page || 1,
    perPage: perPage || 50,
    isFilterOpen: isFilterOpen,
    orderBy: orderBy || '',
    orderDirection: orderDirection || '',
    startOn: startOn || '',
    endOn: endOn || '',
    saved: saved,
    dashboardId: dashboardId,
    showCleared: showCleared,
    orgId: orgId,
    objectId: objectId,
    segOne: segOne,
    segTwo: segTwo,
    segThree: segThree,
    segFour: segFour,
    segFive: segFive,
    orgs: orgs,
    objects: objects,
    projects: projects,
    segOnes: segOnes,
    segTwos: segTwos,
    segThrees: segThrees,
    segFours: segFours,
    segFives: segFives,
    segSixes: segSixes,
    segSevens: segSevens,
    segEights: segEights,
    orgsOperator: orgsOperator,
    objectsOperator: objectsOperator,
    projectsOperator: projectsOperator,
    segOnesOperator: segOnesOperator,
    segTwosOperator: segTwosOperator,
    segThreesOperator: segThreesOperator,
    segFoursOperator: segFoursOperator,
    segFivesOperator: segFivesOperator,
    segSixesOperator: segSixesOperator,
    segSevensOperator: segSevensOperator,
    segEightsOperator: segEightsOperator,
    tag: tag,
    auditReportId: auditReportId,
    riskScore: riskScore,
    analyticsCount: analyticsCount,
    ...Object.keys(memoizedQueryParams).reduce((acc, f) => {
      acc[f] = memoizedQueryParams[f]
      return acc
    }, {}),
    // queryParams.analytics is a joined-id string for the API URL; restore the array form for in-component consumers.
    analytics: analytic || []
  })

  // Update table filters when queryParams change (e.g., when dashboard params load)
  useEffect(() => {
    setTableFilters(prev => {
      return {
        ...prev,
        section_id: sectionId,
        employees: employees || [],
        excludedEmployees: excludedEmployees || [],
        vendors: vendors || [],
        excludedVendors: excludedVendors || [],
        page: defaultParams.page || 1,
        perPage: perPage || 50,
        isFilterOpen: isFilterOpen,
        orderBy: orderBy || '',
        orderDirection: orderDirection || '',
        startOn: startOn || '',
        endOn: endOn || '',
        saved: saved,
        dashboardId: dashboardId,
        showCleared: showCleared,
        orgId: orgId,
        objectId: objectId,
        segOne: segOne,
        segTwo: segTwo,
        segThree: segThree,
        segFour: segFour,
        segFive: segFive,
        orgs: orgs,
        objects: objects,
        projects: projects,
        segOnes: segOnes,
        segTwos: segTwos,
        segThrees: segThrees,
        segFours: segFours,
        segFives: segFives,
        segSixes: segSixes,
        segSevens: segSevens,
        segEights: segEights,
        orgsOperator: orgsOperator,
        objectsOperator: objectsOperator,
        projectsOperator: projectsOperator,
        segOnesOperator: segOnesOperator,
        segTwosOperator: segTwosOperator,
        segThreesOperator: segThreesOperator,
        segFoursOperator: segFoursOperator,
        segFivesOperator: segFivesOperator,
        segSixesOperator: segSixesOperator,
        segSevensOperator: segSevensOperator,
        segEightsOperator: segEightsOperator,
        tag: tag,
        auditReportId: auditReportId,
        riskScore: riskScore,
        analyticsCount: analyticsCount,
        ...Object.keys(memoizedQueryParams).reduce((acc, f) => {
          acc[f] = memoizedQueryParams[f]
          return acc
        }, {}),
        analytics: analytic || []
      }
    }) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedQueryParams])

  useEffect(() => {
    if (onTransactionListIdChange) {
      onTransactionListIdChange(tableFilters.transactionListId)
    }
  }, [tableFilters.transactionListId]) // eslint-disable-line react-hooks/exhaustive-deps

  const tableRef = useRef(null)
  const hasSectionStatusColumn =
    tableColumns?.some((tc) => tc.database_column_name === 'status') ?? false
  const percentColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'percent')
      .map((tc) => tc.database_column_name) || []
  const twoDigitMoneyColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'two_digit_money')
      .map((tc) => tc.database_column_name) || []
  const moneyColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'money')
      .map((tc) => tc.database_column_name) || []
  const accountSegmentColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'account_segment')
      .map((tc) => tc.database_column_name) || []
  const flagCountColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'flag_count')
      .map((tc) => tc.database_column_name) || []
  const riskScoreColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'risk_score')
      .map((tc) => tc.database_column_name) || []
  const textColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'text')
      .map((tc) => tc.database_column_name) || []
  const dateColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'date')
      .map((tc) => tc.database_column_name) || []
  const booleanColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'boolean')
      .map((tc) => tc.database_column_name) || []
  const timeInQueueColumns =
    tableColumns
      ?.filter((tc) => tc.display_as === 'time_in_queue')
      .map((tc) => ({
        database_column_name: tc.database_column_name,
        accessor_key: tc.accessor_key
      })) || []


  // Fetch custom columns for this dashboard
  const { data: fetchedCustomColumns = [] } = useQuery(
    ['customColumns', dashboardId],
    () => customColumnService.getCustomColumns(token, dashboardId),
    {
      enabled: showCustomColumns && !!dashboardId && !!token
    }
  )
  const customColumns = showCustomColumns ? fetchedCustomColumns : []

  // Sort custom columns by their column_order for proper positioning
  const sortedCustomColumns = useMemo(() => {
    return customColumns.sort((a, b) => (a.column_order || 0) - (b.column_order || 0))
  }, [customColumns])

  const transactionQueryParams = useMemo(() => ({
    page: tableFilters.page,
    perPage: tableFilters.perPage,
    orderBy: tableFilters.orderBy,
    orderDirection: tableFilters.orderDirection,
    include_ordering: includeOrdering,
    // Column header filters come off the live tableFilters (which already includes any seeded
    // queryParams), so pages that don't pre-seed queryParams still send them.
    ...transactionFilterParams(tableFilters, { columns: tableColumns, customColumns: sortedCustomColumns }),
    ...requestParams
  }), [tableFilters, tableColumns, sortedCustomColumns, includeOrdering, requestParams])

  const {
    isSuccess,
    isLoading,
    data: transactions
  } = useTransactions({
    params: transactionQueryParams,
    // Pause while the filter panel OR the column manager modal is open; the query
    // re-runs once on close (it goes stale at staleTime 0 and refetches when
    // re-enabled). This keeps editing many columns from firing a transactions
    // fetch per add/remove/reorder on large datasets. The selected-column set is
    // not part of the query key, so the disabled query simply retains its cached
    // rows while the modal is open.
    options: {
      enabled: !isFilterOpen && !columnModalOpen,
      refetchOnWindowFocus: false
    }
  })

  const transactionIds = isSuccess ? transactions.data.map((t) => t.id) : []

  // Create unified column order - use precomputed order if available, otherwise fall back to original logic
  const unifiedColumnOrder = useMemo(() => {
    const addToListItem = { type: 'regular', data: 'add_to_list', sequentialPosition: -1 }

    // If we have a precomputed column order from the dashboard, use it
    if (precomputedColumnOrder && precomputedColumnOrder.length > 0) {
      return showAddToListColumn ? [addToListItem, ...precomputedColumnOrder] : precomputedColumnOrder
    }

    // Fallback to original logic for backward compatibility
    if (!columnOrder || sortedCustomColumns.length === 0) {
      const base = sortedCustomColumns.map(customCol => ({ type: 'custom', data: customCol }))
        .concat(columnOrder.map(columnName => ({ type: 'regular', data: columnName })))
      return showAddToListColumn ? [addToListItem, ...base] : base
    }

    // Create a unified list with all columns and their absolute positions
    const allItems = []

    // Add regular columns with their positions (columnOrder already comes pre-sorted)
    columnOrder.forEach((columnName, index) => {
      allItems.push({
        type: 'regular',
        data: columnName,
        sequentialPosition: index
      })
    })

    // Add custom columns - they should be interspersed based on their saved positions
    sortedCustomColumns.forEach(customCol => {
      allItems.push({
        type: 'custom',
        data: customCol,
        sequentialPosition: customCol.column_order != null ? customCol.column_order : 999
      })
    })

    // Sort by the sequential position to get the correct unified order
    const sortedItems = allItems.sort((a, b) => {
      return a.sequentialPosition - b.sequentialPosition
    })

    // Ensure ThirdLine ID is always first if it exists
    const idIndex = sortedItems.findIndex(item =>
      item.type === 'regular' && item.data === 'id'
    )
    if (idIndex > 0) {
      const idItem = sortedItems.splice(idIndex, 1)[0]
      sortedItems.unshift(idItem)
    }

    // Prepend Add to List column at the start unless opted out
    if (showAddToListColumn) {
      sortedItems.unshift(addToListItem)
    }

    return sortedItems
  }, [columnOrder, sortedCustomColumns, precomputedColumnOrder, showAddToListColumn])

  // Fetch custom column values for current page of transactions
  const { data: fetchedCustomColumnValues = {} } = useQuery(
    ['customColumnValues', dashboardId, transactionIds],
    () => customColumnService.getCustomColumnValues(token, dashboardId, transactionIds),
    {
      enabled: showCustomColumns && !!dashboardId && transactionIds.length > 0 && !!token
    }
  )
  const customColumnValues = showCustomColumns ? fetchedCustomColumnValues : {}

  return (
    <div ref={tableRef}>
      {title.length > 0 && (
        <Row className="my-2">
          <Col>
            {' '}
            <h4>{title}</h4>
          </Col>
          <hr />
        </Row>
      )}
      {(filtersEnabled || downloadButton) && (
        <Card className="mb-4">
          <Card.Body>
            <Row className="g-2">
              {filtersEnabled && (
                <TableFilters
                  filters={filters}
                  tableFilters={tableFilters}
                  setTableFilters={setTableFilters}
                  tableRef={tableRef}
                  defaultTableFilters={defaultTableFilters}
                />
              )}
              <Col sm="auto">
                {downloadButton && (
                  <DownloadTransactions
                    defaultName={`${source} Transactions`}
                    source={source}
                    queryParams={transactionQueryParams}
                  />
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <div className="table-responsive">
        <table
          className="bg-white shadow-sm table mb-0 text-nowrap"
          style={{ border: '1px solid rgb(222 226 230)' }}
        >
          <TableHeader
            tableFilters={tableFilters}
            setTableFilters={setTableFilters}
            columns={columnOrder}
            includeOrdering={includeOrdering}
            tableColumns={tableColumns}
            hasSectionStatusColumn={hasSectionStatusColumn}
            dashboardSetParams={dashboardSetParams}
            analyticsColumns={analyticsColumns}
            municipality={municipality}
            sectionId={sectionId}
            customColumns={sortedCustomColumns}
            unifiedColumnOrder={unifiedColumnOrder}
            dashboardId={dashboardId}
            canEdit={canEdit}
            referenceSearch={referenceSearch}
            onReferenceSearchHandled={onReferenceSearchHandled}
            reportFeatures={reportFeatures}
          />
          <tbody>
            {isLoading && <TableLoading columns={7} />}
            {isSuccess && transactions.data.length === 0 && (
              <tr>
                <td colSpan={columnOrder.length} className="text-center">
                  <div className="my-4">No transactions found</div>
                </td>
              </tr>
            )}
            {isSuccess &&
              transactions.data.map((t, i) => (
                <TableRow
                  transaction={t}
                  key={`row-${t.id}-${i}`}
                  columns={columnOrder}
                  auditReportId={auditReportId}
                  transactions={transactionIds}
                  dashboardId={dashboardId}
                  percentColumns={percentColumns}
                  twoDigitMoneyColumns={twoDigitMoneyColumns}
                  moneyColumns={moneyColumns}
                  accountSegmentColumns={accountSegmentColumns}
                  flagCountColumns={flagCountColumns}
                  riskScoreColumns={riskScoreColumns}
                  textColumns={textColumns}
                  dateColumns={dateColumns}
                  booleanColumns={booleanColumns}
                  timeInQueueColumns={timeInQueueColumns}
                  analyticsColumns={analyticsColumns}
                  customColumns={sortedCustomColumns}
                  customColumnValues={customColumnValues[t.id] || {}}
                  unifiedColumnOrder={unifiedColumnOrder}
                  canEdit={canEdit}
                  hasSectionStatusColumn={hasSectionStatusColumn}
                />
              ))}
          </tbody>
        </table>
      </div>

      <Row className="mt-4">
        <Col>
          {isSuccess && footerEnabled && (
            <TableFooter
              meta={transactions.meta}
              params={tableFilters}
              setParams={setTableFilters}
              disableJump={disableJump}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

TransactionTable.propTypes = {
  section: PropTypes.number,
  vendors: PropTypes.array,
  employees: PropTypes.array,
  analytics: PropTypes.array,
  columns: PropTypes.array.isRequired,
  filtersEnabled: PropTypes.bool,
  auditReportId: PropTypes.string
}

export default TransactionTable
